/**
 * Home route — Cluster-1 landing surface with prototype-aligned density.
 *
 * Spec scope: FR-009 ("The demo home view MUST orient the member
 * without pretending to have fetched curated content."), FR-058 amended
 * by D-049 (photo-forward / title-dominant card layout backed by
 * customer-owned imagery), FR-003 amended by D-050 (no global demo-build
 * pill — local honesty at every surface where data source affects
 * meaning), FR-040 (Finnish-first copy via `useTranslate`).
 *
 * Structure:
 *   - Greeting (`<h2>`) — Layout's TopBar owns the shell `<h1>`. The
 *     greeting reads the active session via `useSession()`; for a
 *     signed-in member or admin it surfaces "<prefix>, <displayName>"
 *     (e.g. "Hyvää huomenta, Matti Meikäläinen"); for an anonymous
 *     session it falls back to a plain welcome ("Tervetuloa"). The
 *     display name comes straight from the `SessionPort` adapter —
 *     the demo adapter seeds Finnish-flavoured placeholder names
 *     matching the prototype's seeded test users. The greeting prefix
 *     is time-of-day-aware (Hyvää huomenta / Hyvää päivää / Hyvää
 *     iltaa) so the surface feels less templated and matches the
 *     prototype's rendered greeting variant.
 *   - Tabs — segmented toggle between "Lakikaveri" and "Jäsenyys ja edut".
 *     Active state locks `bg-tab-active` / `text-tab-active-foreground`
 *     so the toggle reads as a deep-green pill against the muted track,
 *     matching the prototype's tab treatment.
 *   - Per-tab image-card grid — delegated to `HomeCards` (see
 *     `./Home/HomeCards.tsx`). The cards render as `<button>` controls
 *     with full-bleed customer-owned PNGs, a bottom gradient overlay,
 *     and the title overlaid on the gradient. No per-card chip and no
 *     section-level demo subtitle: customer feedback flagged the
 *     additional chrome as misaligned with the prototype's rendered
 *     surface (the proto's cards are clean — title only). This matches
 *     the amended FR-058 (per `docs/governance/proto-divergences.md`
 *     D-PD-003): the conditional clause requiring per-card demo
 *     labels when cards are interactive was retired in favour of the
 *     route-level demo posture (the demo session adapter, the demo
 *     content seed, the /login disclaimer) carrying the transparency
 *     contract. Extracted out of this file to keep Home under the
 *     FR-045 line-count ceiling for `apps/web/src`
 *     routes/pages/components.
 *   - Quick-access buttons — three ~96px cards with icon + title +
 *     subtitle. "Ota yhteyttä" opens the compose wizard (D-120,
 *     FR-023); "Lakiruutu käytössäsi" navigates to the live
 *     `/lakiruutu` surface (029 / D-116, SC-003 "no coming-soon for
 *     /lakiruutu"). The remaining "Työkalut ja laskurit" tile stays
 *     disabled and carries a "Tulossa" badge: its destination does not
 *     exist yet.
 *
 * Quick-access icons are hand-authored inline SVGs — they are not
 * brand mark or photographic content, so the asset-lift narrowing in
 * D-049 does not apply. They use token-backed `text-*` utilities +
 * `currentColor` so the tint tracks the token palette, and each carries
 * a `<title>` element to satisfy the `noSvgWithoutTitle` lint rule.
 */

import type { JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { MemberBanner } from '../content/MemberBanner.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { useComposeWizard } from '../member-messaging/compose/wizard/ComposeWizardProvider.js';
import { HomeOverdueFeeNotice } from '../membership-billing/OverdueFeeNotice.js';
import { Tabs } from '../primitives/Tabs.js';
import { useSession } from '../session/useSession.js';
import { ChevronRightIcon } from '../shell/icons.js';
import { HomeButtons } from './Home/HomeButtons.js';
import { HomeCards } from './Home/HomeCards.js';

/**
 * Tab identifiers for the Lakikaveri / Jäsenyys ja edut split. Mirrors
 * the prototype's URL contract (the proto reads `?view=lakikaveri|muut`)
 * so any deep-link or programmatic navigation can address either tab
 * via `/?tab=lakikaveri` or `/?tab=palvelutEdut` and Home reflects the
 * requested tab on arrival. (Post-017 the menu sheet no longer carries
 * shortcut NavLinks that deep-link here — the two service parents are
 * now expandable disclosures inside the drawer, rendered inline. The
 * `?tab=…` seam stays as the segmented-tab API on Home itself.)
 *
 * The values are stable IDs (not user-facing copy) so they survive
 * locale changes. Default is `palvelutEdut` (026 US1 / FR-001): the home
 * landing opens on "Jäsenyys ja edut" to match the current prototype,
 * with "Lakikaveri" available as the second tab.
 */
const HOME_TABS = ['palvelutEdut', 'lakikaveri'] as const;
type HomeTab = (typeof HOME_TABS)[number];
const DEFAULT_HOME_TAB: HomeTab = 'palvelutEdut';

function isHomeTab(value: string | null): value is HomeTab {
  return value !== null && (HOME_TABS as readonly string[]).includes(value);
}

/**
 * Time-of-day-aware greeting prefix selector. Three windows:
 *   - 04-11: morning ("Hyvää huomenta,")
 *   - 11-17: day ("Hyvää päivää,")
 *   - 17-04: evening ("Hyvää iltaa,")
 *
 * `clock` is passed in to keep the function pure for unit testing —
 * the route call site uses `new Date()` so each render reads the
 * current local time.
 */
export function timeOfDayGreetingKey(
  clock: Date,
): 'home.greeting.morning' | 'home.greeting.day' | 'home.greeting.evening' {
  const hour = clock.getHours();
  if (hour >= 4 && hour < 11) {
    return 'home.greeting.morning';
  }
  if (hour >= 11 && hour < 17) {
    return 'home.greeting.day';
  }
  return 'home.greeting.evening';
}

const decorativeSvgProps = {
  'aria-hidden': true,
  focusable: false,
  xmlns: 'http://www.w3.org/2000/svg',
} as const;

// Quick-access icons — small inline SVGs beside each button's title.
function QuickAccessChatIcon(): JSX.Element {
  return (
    <svg
      {...decorativeSvgProps}
      viewBox="0 0 24 24"
      className="h-10 w-10"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <title>Chat</title>
      <path d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-5 4V6a1 1 0 0 1 1-1z" />
      <circle cx={9} cy={11} r={1} />
      <circle cx={12} cy={11} r={1} />
      <circle cx={15} cy={11} r={1} />
    </svg>
  );
}

function QuickAccessPlayIcon(): JSX.Element {
  return (
    <svg {...decorativeSvgProps} viewBox="0 0 24 24" className="h-10 w-10" fill="currentColor">
      <title>Play</title>
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function QuickAccessCalculatorIcon(): JSX.Element {
  return (
    <svg
      {...decorativeSvgProps}
      viewBox="0 0 24 24"
      className="h-10 w-10"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <title>Calculator</title>
      <rect x={5} y={3} width={14} height={18} rx={2} />
      <path d="M8 7h8" />
      <path d="M8 11h2" />
      <path d="M12 11h2" />
      <path d="M16 11h0" />
      <path d="M8 15h2" />
      <path d="M12 15h2" />
      <path d="M16 15h0" />
      <path d="M8 19h2" />
      <path d="M12 19h2" />
    </svg>
  );
}

const pageClasses = 'flex flex-col gap-6';
const greetingClasses = 'm-0 text-2xl font-bold text-foreground';
const tabsContentClasses = 'pt-4 flex flex-col gap-4';
const quickAccessWrapperClasses = 'flex flex-col gap-3';
// Quick-access row treatment. "Ota yhteyttä" opens the compose wizard
// and "Lakiruutu käytössäsi" navigates to the live `/lakiruutu` route;
// only "Työkalut ja laskurit" renders as `disabled` (its destination is
// not yet implemented). The base style is the enabled-shape so the row
// reads as a real tappable surface — proto-parity for the prototype's
// "go in" cards. The hover tint uses the brand-green `tab-active`
// token at 30%/10% opacity. `cursor-pointer` is explicit per
// Tailwind v4; the `disabled:cursor-not-allowed` modifier outranks it
// when the button is disabled. `transition` + `active:scale-95`
// give the press feedback shape.
// `min-h-24` (not `h-24`): the card holds its proto-anchored visual
// height for short Finnish titles (1-line title + 1-line subtitle fits
// the 96 px minimum cleanly), but grows when longer copy — typically
// the English bundle — pushes the title or subtitle to two lines.
// Pinning a fixed height instead would cause the title and the
// "Coming soon" / "Tulossa" badge to visually overlap on locale
// switch; growing the card preserves the row geometry and keeps every
// affordance reachable.
const quickAccessButtonClasses =
  'flex min-h-24 w-full items-center gap-3 rounded-xl bg-card text-card-foreground border border-border px-4 py-3 text-left ' +
  'cursor-pointer transition active:scale-95 ' +
  'hover:border-tab-active/30 hover:bg-tab-active/10 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';
const quickAccessIconWrapperClasses =
  'flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-muted text-tab-active';
const quickAccessBodyClasses = 'flex flex-1 flex-col items-start gap-1 overflow-hidden';
const quickAccessTitleClasses = 'text-base font-bold text-foreground';
const quickAccessSubtitleClasses = 'text-sm text-muted-foreground';
// `self-start` keeps the badge at the start of the cross-axis (its
// own line in the body column) rather than centred against tall
// title/subtitle wraps, which would shift on every locale change.
// `whitespace-nowrap` prevents the chip itself from word-breaking
// (the chip is a single label, not flowing copy).
const quickAccessBadgeClasses =
  'self-start inline-flex items-center rounded-md bg-tab-active px-2 py-0.5 text-xs font-medium text-tab-active-foreground whitespace-nowrap';
// Trailing chevron — proto-parity affordance signalling "this row leads
// onward". Shrink-0 so the chevron does not collapse when the body
// content is wide; muted-foreground tint and opacity keep the disabled
// tools row's "Tulossa" badge more prominent than the onward affordance.
const quickAccessChevronClasses = 'h-5 w-5 shrink-0 text-muted-foreground opacity-60';

export function Home(): JSX.Element {
  const t = useTranslate();
  const session = useSession();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The quick-access contact row opens the compose wizard (027 US6 / D-120,
  // FR-023) — it no longer navigates to the legacy /contact journey.
  const composeWizard = useComposeWizard();

  // Tab routing — read the `tab` query param so any deep-link or
  // programmatic navigation can address either tab via
  // `/?tab=lakikaveri` or `/?tab=palvelutEdut`. Falling back to the
  // default keeps the bare `/` URL stable. `setSearchParams({ tab })`
  // writes the param back when the user activates a trigger directly
  // so the URL stays a faithful mirror of the visible state and the
  // back/forward buttons behave naturally. (Post-017 the menu sheet's
  // service parents are expandable disclosures rather than deep-link
  // shortcuts, so the `?tab=…` consumers today are URL bookmarks /
  // shared links, not in-app shortcuts.)
  const tabParam = searchParams.get('tab');
  const activeTab: HomeTab = isHomeTab(tabParam) ? tabParam : DEFAULT_HOME_TAB;
  const handleTabChange = (next: string): void => {
    if (!isHomeTab(next)) {
      return;
    }
    // `replace: true` keeps tab toggles out of the back-button history
    // so Back from Home goes to the previous route, not to the prior
    // tab on Home. `setSearchParams` accepts a `URLSearchParams`-like
    // record; passing `{ tab: next }` clears any other params on /.
    setSearchParams({ tab: next }, { replace: true });
  };

  // Greeting routing. The session adapter is the source of truth for
  // the display name; for a member or admin we surface
  // "<prefix>, <displayName>" (e.g. "Hyvää huomenta, Matti Meikäläinen").
  // For anonymous sessions we fall back to a plain welcome.
  //
  // The prefix is time-of-day-aware: morning (04-11) / day (11-17) /
  // evening (17-04). The thresholds match the prototype's rendered
  // variant so the greeting reads naturally throughout the day.
  //
  // `useTranslate` does not support interpolation today — the lookup
  // is strictly key→string. So the prefix and the name are
  // concatenated here in JSX rather than threading an interpolation
  // token through the bundle. The prefix's trailing comma is part of
  // the bundle string; the joining whitespace is a literal space
  // character.
  // Greeting uses the first word of the display name (the personal
  // first name) — matches the prototype's rendered "Hyvää huomenta,
  // Matti" style. Falls back to the full display name if it contains
  // no whitespace (e.g., a single-word handle).
  // Gate the personalised greeting on a SIGNED-IN state (member or admin), not
  // the negative of `anonymous`: the `loading` arm (the BFF session not yet
  // resolved) carries no `displayName`, so it must take the same
  // identity-unknown path as anonymous (empty first name, anonymous greeting)
  // rather than reading a field it does not have. In practice Home renders
  // behind RouteGuard, which holds a `loading` session on its placeholder, so
  // this guards the transient/edge case rather than a steady-state render.
  const isSignedIn = session.state.kind === 'member' || session.state.kind === 'admin';
  const firstName = isSignedIn ? (session.state.displayName.split(' ')[0] ?? '') : '';
  const greetingText = isSignedIn
    ? `${t(timeOfDayGreetingKey(new Date()))} ${firstName}`
    : t('home.greeting.anonymous');

  return (
    <section className={pageClasses} aria-labelledby="home-greeting">
      <h2 id="home-greeting" className={greetingClasses}>
        {greetingText}
      </h2>

      {/* 057: above the CMS strip; renders nothing unless the server says show. */}
      <HomeOverdueFeeNotice />

      {/* US14 banner strip: in-flow above the tabs; Home renders both scopes,
          the shell mount skips "/" so this is the only strip here. */}
      <MemberBanner placement="home" />

      <Tabs.Root variant="pill" value={activeTab} onValueChange={handleTabChange}>
        <Tabs.List aria-label={t('home.tabs.label')}>
          <Tabs.Trigger value="palvelutEdut">{t('home.tabs.palvelutEdut')}</Tabs.Trigger>
          <Tabs.Trigger value="lakikaveri">{t('home.tabs.lakikaveri')}</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="palvelutEdut" className={tabsContentClasses}>
          <HomeCards tab="palvelutEdut" t={t} onContact={composeWizard.open} />
          <HomeButtons tab="palvelutEdut" />
        </Tabs.Content>

        <Tabs.Content value="lakikaveri" className={tabsContentClasses}>
          <HomeCards tab="lakikaveri" t={t} onContact={composeWizard.open} />
          <HomeButtons tab="lakikaveri" />
        </Tabs.Content>
      </Tabs.Root>

      <div className={quickAccessWrapperClasses}>
        <button
          type="button"
          className={quickAccessButtonClasses}
          onClick={() => composeWizard.open()}
        >
          <span className={quickAccessIconWrapperClasses}>
            <QuickAccessChatIcon />
          </span>
          <span className={quickAccessBodyClasses}>
            <span className={quickAccessTitleClasses}>{t('home.quickAccess.contact.title')}</span>
            <span className={quickAccessSubtitleClasses}>
              {t('home.quickAccess.contact.subtitle')}
            </span>
          </span>
          <ChevronRightIcon aria-hidden="true" className={quickAccessChevronClasses} />
        </button>

        <button
          type="button"
          className={quickAccessButtonClasses}
          onClick={() => navigate('/lakiruutu')}
        >
          <span className={quickAccessIconWrapperClasses}>
            <QuickAccessPlayIcon />
          </span>
          <span className={quickAccessBodyClasses}>
            <span className={quickAccessTitleClasses}>{t('home.quickAccess.lakiruutu.title')}</span>
            <span className={quickAccessSubtitleClasses}>
              {t('home.quickAccess.lakiruutu.subtitle')}
            </span>
          </span>
          <ChevronRightIcon aria-hidden="true" className={quickAccessChevronClasses} />
        </button>

        <button type="button" disabled className={quickAccessButtonClasses}>
          <span className={quickAccessIconWrapperClasses}>
            <QuickAccessCalculatorIcon />
          </span>
          <span className={quickAccessBodyClasses}>
            <span className={quickAccessTitleClasses}>{t('home.quickAccess.tools.title')}</span>
            <span className={quickAccessSubtitleClasses}>
              {t('home.quickAccess.tools.subtitle')}
            </span>
            <span className={quickAccessBadgeClasses}>{t('home.quickAccess.deferredBadge')}</span>
          </span>
          <ChevronRightIcon aria-hidden="true" className={quickAccessChevronClasses} />
        </button>
      </div>
    </section>
  );
}
